import { Box, Flex, ThemeUIStyleObject } from 'theme-ui'
import { Text } from 'components'
import { DeviceType, UserType, ViewMode } from 'types'
import { ClearDevicesBtn } from './ClearDevicesBtn'
import { IndexRecordBtn } from './IndexRecordBtn'
import { SnapshotBtn } from './SnapshotBtn'
import { ReinstateBtn } from './ReinstateBtn'
import { DisableBtn } from './DisableBtn'
import { DCProgressBtn } from './DCProgressBtn'
import { DCProgressBtnV3 } from './DCProgressBtnV3'
import { isV2OrHigher } from 'utils/dc'
import { ResendInstructionsBtn } from './ResendInstructionsBtn'
import { useCallback, useEffect, useState } from 'react'
import { ErrorMessage } from 'components/Form'
import { STUDENT_DC, TEACHER, TEACHER_DC } from '../../../constants'
import { ReactivateTMATTBtn } from './ReactivateTMATTBtn'
import { DeactivateTMATTBtn } from './DeactivateTMATTBtn'
import { EnrollTMATTBtn } from './EnrollTMATTBtn'
import { DeleteBtn } from './DeleteBtn'
import { useHistory } from 'react-router-dom'
import { VIEW } from '../../../constants'

export type AccountActionsProps = {
  userId: string
  device?: DeviceType
  disabled?: boolean
  trainingStatus?: string | undefined
  isCustomerSupportUser?: boolean
  userType?: UserType
  studentStatus?: string | undefined
  dcProgress?: any
  dcVersion?: string
  viewMode?: ViewMode
}

export function AccountActions({
  userId,
  device,
  isCustomerSupportUser = false,
  disabled = false,
  userType = undefined,
  trainingStatus = undefined,
  studentStatus = undefined,
  dcProgress,
  dcVersion,
  viewMode = 'edit',
}: AccountActionsProps) {
  const [message, setDoneMessage] = useState<string>('')
  const [error, setErrorMessage] = useState<string>('')
  const history = useHistory()

  useEffect(() => {
    //update in effect!
    setErrorMessage('')
  }, [userType, studentStatus])

  useEffect(() => {
    if (message) {
      const id = setTimeout(() => setDoneMessage(''), 7000)
      return () => clearTimeout(id)
    }
  }, [message, setDoneMessage])

  const navigateToDashboard = useCallback(() => {
    history.push({ pathname: '/', state: null })
  }, [history])

  const displayError = useCallback(
    (message: string) => {
      setErrorMessage(message)
      setDoneMessage('')
    },
    [setDoneMessage, setErrorMessage]
  )

  const displayMessage = useCallback(
    (message: string) => {
      setDoneMessage(message)
      setErrorMessage('')
    },
    [setDoneMessage, setErrorMessage]
  )

  const isDigitalCourseStudent = userType === STUDENT_DC
  const isTeacher = userType === TEACHER || userType === TEACHER_DC
  const isStudentDisabled = studentStatus === 'disabled'
  const showResendDownloadInstructionsButton = studentStatus !== 'waiting'
  const showAdjustProgressButton =
    viewMode === VIEW &&
    isDigitalCourseStudent &&
    (studentStatus === 'progress' ||
      studentStatus === 'completed-review' ||
      studentStatus === 'unlocked' ||
      studentStatus === 'review')
  const showReinstateButton =
    isDigitalCourseStudent &&
    (studentStatus === 'completed' || studentStatus === 'incomplete')
  const statusForEnrollButton = [
    'unknown',
    'waiting',
    undefined,
    'completed',
    'expired',
  ]
  const statusForDeactivateButton = [
    'progress',
    'signature-pending',
    'pre-course',
  ]
  const statusForReactivateButton = ['deactivated', 'expired']
  let showReactivateButton = false
  let showEnrollButton = false
  let showDeactivateButton = false
  if (isTeacher) {
    showEnrollButton = statusForEnrollButton.includes(trainingStatus)
    showDeactivateButton =
      !!trainingStatus && statusForDeactivateButton.includes(trainingStatus)
    showReactivateButton =
      !!trainingStatus && statusForReactivateButton.includes(trainingStatus)
  }

  return (
    <>
      <Flex
        variant={`forms.section.${viewMode}`}
        sx={
          {
            mx: '-4px !important',
            '& > *': {
              variant: [
                'forms.inputGroup.edit',
                `forms.inputGroup.${viewMode}`,
              ],
              m: '4px !important',
            },
          } as ThemeUIStyleObject
        }
      >
        {isCustomerSupportUser && (
          <ClearDevicesBtn
            userId={userId}
            disabled={disabled}
            onDone={displayMessage}
            onError={displayError}
          />
        )}
        {isCustomerSupportUser && (
          <IndexRecordBtn
            userId={userId}
            disabled={disabled}
            onDone={displayMessage}
            onError={displayError}
          />
        )}
        {isCustomerSupportUser && (
          <SnapshotBtn
            userId={userId}
            disabled={disabled}
            onDone={(data) => {
              // Create element with <a> tag
              const link = document.createElement('a')

              // Create a blog object with the file content which you want to add to the file
              const file = new Blob([JSON.stringify(data, undefined, 2)], {
                type: 'application/json',
              })

              // Add file content in the object URL
              link.href = URL.createObjectURL(file)

              // Add file name
              link.download = `${userId}.json`

              // Add click event to <a> tag to save file.
              link.click()
              URL.revokeObjectURL(link.href)
            }}
            onError={displayError}
          />
        )}
        {showReinstateButton && (
          <ReinstateBtn
            userId={userId}
            disabled={disabled}
            onDone={displayMessage}
            onError={displayError}
          />
        )}
        {isCustomerSupportUser && !isStudentDisabled && (
          <DisableBtn
            userType={userType}
            userId={userId}
            disabled={disabled}
            onDone={displayMessage}
            onError={displayError}
          />
        )}
        {isCustomerSupportUser && (
          <DeleteBtn
            userId={userId}
            disabled={disabled}
            onDone={(message) => {
              displayMessage(message)

              setTimeout(navigateToDashboard, 5000)
            }}
            onError={displayError}
          />
        )}
        {isCustomerSupportUser && isTeacher && (
          <>
            {showReactivateButton && (
              <ReactivateTMATTBtn
                userId={userId}
                userType={userType}
                disabled={disabled}
                onDone={displayMessage}
                onError={displayError}
              />
            )}
            {showDeactivateButton && (
              <DeactivateTMATTBtn
                userId={userId}
                userType={userType}
                disabled={disabled}
                onDone={displayMessage}
                onError={displayError}
              />
            )}
            {showEnrollButton && (
              <EnrollTMATTBtn
                userId={userId}
                disabled={disabled}
                onDone={displayMessage}
                onError={displayError}
              />
            )}
          </>
        )}
        {showResendDownloadInstructionsButton && !isStudentDisabled && (
          <ResendInstructionsBtn
            userId={userId}
            device={device}
            disabled={disabled}
            onDone={displayMessage}
            onError={displayError}
          />
        )}
        {showAdjustProgressButton &&
          (isV2OrHigher(dcVersion) ? (
            <DCProgressBtnV3 userId={userId} disabled={disabled} />
          ) : (
            <DCProgressBtn
              userId={userId}
              dcProgress={dcProgress}
              disabled={disabled}
            />
          ))}
      </Flex>
      {message ? (
        <Box sx={{ pt: 5 }}>
          <Text>{message}</Text>
        </Box>
      ) : (
        <ErrorMessage sx={{ pt: 5 }}>{error}</ErrorMessage>
      )}
    </>
  )
}
